/**
 * features/hr/PayrollPeriods.tsx
 * ------------------------------------------------------------------
 * HR PAYROLL — month close / archive control. A rolling list of
 * payroll months with their lock status and the close/reopen actions.
 *
 *   - Months auto-close at month-end (date-derived; no action needed).
 *   - The current month can be closed early.
 *   - A closed/elapsed month can be REOPENED by an admin only, with a
 *     reason, recorded in the payroll audit log; reopened months can
 *     be re-closed when done.
 *
 * Renders embedded in the Payroll hub.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { Lock, Unlock, RotateCcw, ShieldAlert, X, Check } from 'lucide-react';
import { SoftCard, glassFieldClass } from '@/components/ui/glass';
import { Spinner } from '@/components/ui/primitives';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { useAuth } from '@/contexts/AuthContext';
import { MONTH_LABELS } from '@/lib/hrPayroll';
import { lockLabel, lockDescription, type PeriodLockState } from '@/lib/payrollPeriod';
import { usePayrollPeriods, closePayrollPeriod, reopenPayrollPeriod } from '@/features/hr/usePayrollPeriods';

function badgeClass(state: PeriodLockState): string {
  if (state.reason === 'reopened') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  if (state.locked) return 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  if (state.reason === 'future') return 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
}

export default function PayrollPeriods() {
  const { userRole, currentUser } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'systemAdmin';
  const actor = { id: currentUser?.uid ?? 'unknown', email: currentUser?.email ?? undefined };
  const { loading, getState } = usePayrollPeriods();

  const [busy, setBusy] = useState<string | null>(null);
  const [reopen, setReopen] = useState<{ year: number; month: number } | null>(null);
  const [reason, setReason] = useState('');
  const [save, setSave] = useState<{ show: boolean; status: 'saving' | 'success' | 'error'; message: string }>({
    show: false, status: 'saving', message: '',
  });

  // Rolling window: next month (future) down to 12 months back, newest first.
  const periods = useMemo(() => {
    const out: { year: number; month: number }[] = [];
    const now = new Date();
    for (let offset = 1; offset >= -12; offset--) {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      out.push({ year: d.getFullYear(), month: d.getMonth() + 1 });
    }
    return out;
  }, []);

  async function doClose(year: number, month: number) {
    const key = `${year}_${month}`;
    setBusy(key);
    setSave({ show: true, status: 'saving', message: 'Closing payroll month…' });
    try {
      await closePayrollPeriod(year, month, actor);
      setSave({ show: true, status: 'success', message: `${MONTH_LABELS[month - 1]} ${year} closed.` });
    } catch (e) {
      setSave({ show: true, status: 'error', message: e instanceof Error ? e.message : 'Close failed' });
    } finally {
      setBusy(null);
    }
  }

  async function doReopen() {
    if (!reopen || !reason.trim()) return;
    const { year, month } = reopen;
    const key = `${year}_${month}`;
    setBusy(key);
    setSave({ show: true, status: 'saving', message: 'Reopening payroll month…' });
    try {
      await reopenPayrollPeriod(year, month, actor, reason.trim());
      setSave({ show: true, status: 'success', message: `${MONTH_LABELS[month - 1]} ${year} reopened.` });
      setReopen(null);
      setReason('');
    } catch (e) {
      setSave({ show: true, status: 'error', message: e instanceof Error ? e.message : 'Reopen failed' });
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return <div className="flex justify-center py-16"><Spinner label="Loading periods…" /></div>;
  }

  return (
    <div className="space-y-4">
      <SaveConfirmation
        show={save.show}
        status={save.status}
        message={save.message}
        onDismiss={() => setSave((s) => ({ ...s, show: false }))}
      />

      <SoftCard hover={false}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Payroll months lock automatically at month-end — payslips for a closed month become read-only.
          {' '}A deduction added before a month&rsquo;s payslips have been produced is included in that
          month&rsquo;s run; there is no cut-off day. To start one later, set its first month
          explicitly when you save it.
          {isAdmin
            ? ' As an admin you can close the current month early, or reopen a closed month (with a reason) to make corrections, then re-close it.'
            : ' Reopening a closed month to make corrections requires an administrator.'}
        </p>
      </SoftCard>

      <SoftCard pad="p-0" hover={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="py-2 px-4 font-medium">Month</th>
                <th className="py-2 px-4 font-medium">Status</th>
                <th className="py-2 px-4 font-medium">Detail</th>
                <th className="py-2 px-4 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
              {periods.map(({ year, month }) => {
                const state = getState(year, month);
                const key = `${year}_${month}`;
                const rowBusy = busy === key;
                return (
                  <tr key={key}>
                    <td className="py-2.5 px-4 text-gray-800 dark:text-gray-100 font-medium">
                      {MONTH_LABELS[month - 1]} {year}
                    </td>
                    <td className="py-2.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badgeClass(state)}`}>
                        {state.locked ? <Lock size={11} /> : <Unlock size={11} />} {lockLabel(state)}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 text-xs text-gray-500 dark:text-gray-400">{lockDescription(state)}</td>
                    <td className="py-2.5 px-4 text-right">
                      {/* Close early: current open month */}
                      {!state.locked && state.reason === 'current' && (
                        <button
                          disabled={rowBusy}
                          onClick={() => doClose(year, month)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50"
                        >
                          <Lock size={12} /> Close now
                        </button>
                      )}
                      {/* Re-close a reopened month (admin) */}
                      {!state.locked && state.reason === 'reopened' && isAdmin && (
                        <button
                          disabled={rowBusy}
                          onClick={() => doClose(year, month)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-[var(--accent-strong,#1b3a6b)] hover:bg-[var(--accent-strong-hover,#162e52)] rounded-lg disabled:opacity-50"
                        >
                          <Lock size={12} /> Re-close
                        </button>
                      )}
                      {/* Reopen a locked month (admin only) */}
                      {state.locked && isAdmin && (
                        <button
                          disabled={rowBusy}
                          onClick={() => { setReopen({ year, month }); setReason(''); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 rounded-lg disabled:opacity-50"
                        >
                          <RotateCcw size={12} /> Reopen
                        </button>
                      )}
                      {state.locked && !isAdmin && (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                          <ShieldAlert size={12} /> Admin only
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SoftCard>

      {/* Reopen reason modal */}
      {reopen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setReopen(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                Reopen {MONTH_LABELS[reopen.month - 1]} {reopen.year}
              </h3>
              <button onClick={() => setReopen(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              This unlocks the month for corrections and is recorded in the payroll audit log. Re-close it when done.
            </p>
            <label className="block">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Reason (required)</span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Correcting an over-stated allowance on one payslip."
                className={glassFieldClass('mt-1 w-full')}
              />
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReopen(null)} className="px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl">
                Cancel
              </button>
              <button
                onClick={doReopen}
                disabled={!reason.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-50"
              >
                <Check size={15} /> Reopen month
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
